import { Maybe } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { inventoryDatasourceOperations } from '../datasourceOperations';
import { CellEditorProps } from './types';
import { Locale, localeCompareNumericLowercase } from '@ardoq/locale';
import {
  APIComponentAttributes,
  APIComponentType,
  ArdoqId,
} from '@ardoq/api-types';
import { SelectOption, SelectOptionsOrGroups } from '@ardoq/select';
import { ReferenceColumnDirection } from '../types';
import { toInventoryReferenceRepresentation } from '../toInventoryReferenceRepresentation';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { ComponentSuggestionWithBreadcrumbs } from '@ardoq/suggestions-api';

const getValue = <T = unknown>({ value }: CellEditorProps<T>): Maybe<T> =>
  value ?? null;

const componentTypeToSelectOption = (
  componentType: APIComponentType
): SelectOption<string> => ({
  label: componentType.name,
  value: componentType.id,
});

/**
 * Use this function to get component type options for the component type selector in the cell editor
 */
const getTypeOptionsForCurrentWorkspace = (
  props: CellEditorProps<string>,
  locale: Locale
) => {
  const scopeData = props.context.scopeData;
  const currentWorkspaceId = props.data.rootWorkspace;
  return enhancedScopeDataOperations
    .getAPIComponentTypesByWorkspaceId(scopeData, currentWorkspaceId)
    .map(componentTypeToSelectOption)
    .sort((a, b) => localeCompareNumericLowercase(a.label, b.label, locale));
};

/**
 * Use this function to get component type options for the component type selector in the cell editor for unpersisted components.
 * The workspace is editable before persisting the component, so we don't restrict type to selected workspace.
 */
const getTypeOptionsForAllLoadedWorkspaces = (
  props: CellEditorProps<string>,
  locale: Locale
): SelectOptionsOrGroups<string> => {
  const { scopeData, datasourceSelection, accessControl } = props.context;
  const workspaceIdsFromDatasource =
    inventoryDatasourceOperations.getWorkspaceIdsFromDatasource(
      datasourceSelection
    );

  return Object.entries(scopeData.typesByModelId)
    .filter(([key]) => {
      const workspace = scopeData.workspacesByModelId[key];
      return (
        workspace &&
        accessControl.canEditWorkspace(workspace._id) &&
        workspaceIdsFromDatasource.includes(workspace._id)
      );
    })
    .map(([key, value]) => {
      return {
        label: scopeData.workspacesByModelId[key]?.name ?? '',
        options: Object.values(value.componentTypesById)
          .map(componentTypeToSelectOption)
          .filter(option => {
            if (datasourceSelection.dataSourceType !== 'componentType') {
              return true;
            }
            return datasourceSelection.componentTypeIds.includes(option.value);
          })
          .sort((a, b) =>
            localeCompareNumericLowercase(a.label, b.label, locale)
          ),
      };
    });
};

const getComponentTypeNameById = (
  props: CellEditorProps<any>,
  componentTypeId: string
): string | undefined => {
  const typesByModelId = props.context.scopeData.typesByModelId;
  const [componentType] = Object.values(typesByModelId)
    .map(types => types.componentTypesById[componentTypeId])
    .filter(Boolean);
  return componentType?.name;
};

const getWorkspaceAndModelIdAndComponentTypeIdForSelectedType = (
  props: CellEditorProps<string>,
  componentTypId: string
): {
  modelId: ArdoqId;
  workspaceId: ArdoqId;
} => {
  const typesByModelId = props.context.scopeData.typesByModelId;

  const modelId = Object.keys(typesByModelId).find(modelId =>
    Object.values(typesByModelId[modelId].componentTypesById).some(
      componentType => componentType.id === componentTypId
    )
  );

  if (!modelId) {
    throw new Error(
      `Invalid scope data - Could not find model id for component type id ${modelId}`
    );
  }

  const workspaceId =
    enhancedScopeDataOperations.getWorkspaceIdByComponentModelId(
      props.context.scopeData,
      modelId
    )!;

  if (!workspaceId) {
    throw new Error(
      `Invalid scope data - Could not find workspace for model id ${workspaceId}`
    );
  }
  return {
    modelId,
    workspaceId,
  };
};

const getWorkspaceOptions = (
  props: CellEditorProps<string>,
  locale: Locale
) => {
  const openedWorkspaces =
    inventoryDatasourceOperations.getWorkspaceIdsFromDatasource(
      props.context.datasourceSelection
    );
  return props.context.scopeData.workspaces
    .filter(workspace => openedWorkspaces.includes(workspace._id))
    .filter(({ _id }) => props.context.accessControl.canEditWorkspace(_id))
    .map(workspace => ({
      label: workspace.name,
      value: workspace._id,
    }))
    .sort((a, b) => localeCompareNumericLowercase(a.label, b.label, locale));
};

const isPinnedTopRow = (props: CellEditorProps<any>) => {
  const id = props.data._id;
  const pinnedRows: APIComponentAttributes[] | undefined =
    props.api.getGridOption('pinnedTopRowData');
  return Boolean(pinnedRows?.some(row => row._id === id));
};

const toSourceTargetAndReferenceType = (
  scopeData: EnhancedScopeData,
  editedComponent: APIComponentAttributes,
  referencedComponentId: ArdoqId,
  direction: ReferenceColumnDirection,
  referenceTypeName: string,
  componentSuggestions: ComponentSuggestionWithBreadcrumbs[]
) => {
  const { source, target, type } = toInventoryReferenceRepresentation({
    scopeData,
    editedComponent,
    referencedComponentId,
    direction,
    referenceTypeName,
    componentSuggestions,
  });
  if (type === undefined) {
    throw new Error(' Reference type not found.');
  }
  return { source, target, type };
};

export const cellEditorPropsOperations = {
  getValue,
  getTypeOptionsForCurrentWorkspace,
  getTypeOptionsForAllLoadedWorkspaces,
  getWorkspaceOptions,
  getWorkspaceAndModelIdAndComponentTypeIdForSelectedType,
  getComponentTypeNameById,
  isPinnedTopRow,
  toSourceTargetAndReferenceType,
};
